import type { ConditionType, MgmCampaign, MgmProgram, MgmPublicRules, MgmRole, ValidationMode } from './types';
import { parseAmountInput } from './program';

/**
 * Formulário da campanha do Indique e Ganhe (master).
 *
 * O backend guarda, por papel, uma LISTA de condições (motor extensível). O
 * formulário expõe o que existe hoje como uma escolha simples por papel —
 * "ganha no cadastro" ou "ganha na 1ª compra" — e converte para a lista.
 */

export type RewardTrigger = Extract<ConditionType, 'registered' | 'first_purchase'>;

export interface RoleForm {
  enabled: boolean;
  amount: string;
  trigger: RewardTrigger;
}

export interface CampaignFormState {
  name: string;
  active: boolean;
  /** YYYY-MM-DD (DatePicker). */
  startsAt: string;
  /** YYYY-MM-DD ou '' (sem término). */
  endsAt: string;
  referrer: RoleForm;
  referred: RoleForm;
  validationMode: ValidationMode;
  holdHours: string;
  clawback: boolean;
  clawbackDays: string;
  /** R$; '' = sem mínimo. */
  minAmount: string;
  /** Dias; '' = sem prazo. */
  withinDays: string;
  title: string;
  subtitle: string;
  referrerPush: string;
  referredPush: string;
}

/** Textos padrão (os mesmos do backend, `services/mgmProgram.js`). */
export const DEFAULT_COPY = {
  title: 'Indique e ganhe',
  subtitle: 'Convide seus amigos para o programa e ganhe recompensas.',
  referrerPush: 'Sua indicação deu certo! Você ganhou {amount} porque {name} entrou no programa.',
  referredPush: 'Boas-vindas! Você ganhou {amount} por ter sido indicado(a) por {name}.',
} as const;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Data local YYYY-MM-DD (o DatePicker trabalha no fuso do navegador). */
export function toDateInput(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Início do dia local → ISO. */
function startOfDayIso(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}

/** Fim do dia local → ISO (a campanha vale o dia inteiro do término). */
function endOfDayIso(date: string): string {
  return new Date(`${date}T23:59:59`).toISOString();
}

export function emptyForm(today: Date = new Date()): CampaignFormState {
  return {
    name: 'Indique e ganhe',
    active: true,
    startsAt: toDateInput(today),
    endsAt: '',
    referrer: { enabled: true, amount: '', trigger: 'first_purchase' },
    referred: { enabled: true, amount: '', trigger: 'registered' },
    validationMode: 'hold',
    holdHours: '24',
    clawback: false,
    clawbackDays: '7',
    minAmount: '',
    withinDays: '30',
    ...DEFAULT_COPY,
  };
}

function triggerOf(conditions: { type: ConditionType }[]): RewardTrigger {
  return conditions.some((c) => c.type === 'first_purchase') ? 'first_purchase' : 'registered';
}

function amountText(program: MgmProgram, amount: number): string {
  if (!amount) return '';
  return program === 'cashback' ? amount.toFixed(2).replace('.', ',') : String(amount);
}

export function campaignToForm(c: MgmCampaign): CampaignFormState {
  const v = c.purchaseRule.validation;
  return {
    name: c.name,
    active: c.status === 'active',
    startsAt: toDateInput(c.startsAt),
    endsAt: toDateInput(c.endsAt),
    referrer: {
      enabled: c.rewards.referrer.enabled,
      amount: amountText(c.program, c.rewards.referrer.amount),
      trigger: triggerOf(c.rewards.referrer.conditions),
    },
    referred: {
      enabled: c.rewards.referred.enabled,
      amount: amountText(c.program, c.rewards.referred.amount),
      trigger: triggerOf(c.rewards.referred.conditions),
    },
    validationMode: v.mode,
    holdHours: String(v.holdHours),
    clawback: v.clawback,
    clawbackDays: String(v.clawbackDays),
    minAmount: c.purchaseRule.minAmount ? String(c.purchaseRule.minAmount).replace('.', ',') : '',
    withinDays: c.purchaseRule.withinDays ? String(c.purchaseRule.withinDays) : '',
    title: c.copy.title,
    subtitle: c.copy.subtitle,
    referrerPush: c.copy.referrerPush,
    referredPush: c.copy.referredPush,
  };
}

/** Algum papel habilitado depende da 1ª compra? (mostra as regras de compra) */
export function needsPurchaseRules(form: CampaignFormState): boolean {
  return (
    (form.referrer.enabled && form.referrer.trigger === 'first_purchase') ||
    (form.referred.enabled && form.referred.trigger === 'first_purchase')
  );
}

export function validateIdentity(form: CampaignFormState): string[] {
  const errors: string[] = [];
  if (!form.name.trim()) errors.push('Informe o nome da campanha.');
  if (form.name.trim().length > 80) errors.push('O nome pode ter até 80 caracteres.');
  if (!form.startsAt) errors.push('Informe a data de início.');
  if (form.endsAt && form.startsAt && form.endsAt < form.startsAt) {
    errors.push('A data de término precisa ser depois do início.');
  }
  return errors;
}

const ROLE_LABEL: Record<MgmRole, string> = { referrer: 'quem indica', referred: 'quem é indicado' };

export function validateRewards(form: CampaignFormState, program: MgmProgram): string[] {
  const errors: string[] = [];
  if (!form.referrer.enabled && !form.referred.enabled) {
    errors.push('Habilite a recompensa de pelo menos um dos lados.');
  }
  (['referrer', 'referred'] as const).forEach((role) => {
    const r = form[role];
    if (r.enabled && parseAmountInput(program, r.amount) === null) {
      errors.push(
        program === 'cashback'
          ? `Informe um valor válido para ${ROLE_LABEL[role]}.`
          : `Informe uma quantidade inteira para ${ROLE_LABEL[role]}.`,
      );
    }
  });
  return errors;
}

function intInRange(raw: string, min: number, max: number): number | null {
  if (!raw.trim()) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

export function validatePurchase(form: CampaignFormState): string[] {
  if (!needsPurchaseRules(form)) return [];
  const errors: string[] = [];
  if (form.validationMode === 'hold' && intInRange(form.holdHours, 1, 720) === null) {
    errors.push('A carência deve ser de 1 a 720 horas.');
  }
  if (form.validationMode === 'immediate' && form.clawback && intInRange(form.clawbackDays, 1, 90) === null) {
    errors.push('A janela de estorno deve ser de 1 a 90 dias.');
  }
  if (form.withinDays.trim() && intInRange(form.withinDays, 1, 365) === null) {
    errors.push('O prazo da 1ª compra deve ser de 1 a 365 dias.');
  }
  if (form.minAmount.trim()) {
    const min = Number(form.minAmount.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(min) || min < 0) errors.push('Valor mínimo da compra inválido.');
  }
  return errors;
}

function conditionsOf(trigger: RewardTrigger): { type: ConditionType }[] {
  return trigger === 'first_purchase' ? [{ type: 'registered' }, { type: 'first_purchase' }] : [{ type: 'registered' }];
}

/** Corpo do PUT/POST `/mgm/admin/:idPassbook/campaigns`. */
export function formToPayload(form: CampaignFormState, program: MgmProgram) {
  const role = (r: RoleForm) => ({
    enabled: r.enabled,
    amount: r.enabled ? parseAmountInput(program, r.amount) ?? 0 : 0,
    conditions: conditionsOf(r.trigger),
  });
  const min = form.minAmount.trim() ? Number(form.minAmount.replace(/\./g, '').replace(',', '.')) : null;
  return {
    name: form.name.trim(),
    status: form.active ? 'active' : 'inactive',
    startsAt: startOfDayIso(form.startsAt),
    endsAt: form.endsAt ? endOfDayIso(form.endsAt) : null,
    rewards: { referrer: role(form.referrer), referred: role(form.referred) },
    purchaseRule: {
      minAmount: min && min > 0 ? min : null,
      withinDays: intInRange(form.withinDays, 1, 365),
      validation: {
        mode: form.validationMode,
        holdHours: intInRange(form.holdHours, 1, 720) ?? 24,
        clawback: form.validationMode === 'immediate' && form.clawback,
        clawbackDays: intInRange(form.clawbackDays, 1, 90) ?? 7,
      },
    },
    copy: {
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      referrerPush: form.referrerPush.trim(),
      referredPush: form.referredPush.trim(),
    },
  };
}

export type CampaignPhase = 'live' | 'scheduled' | 'ended' | 'inactive';

/** Em que pé a campanha está hoje (a API só aplica a que está `live`). */
export function campaignPhase(
  c: Pick<MgmCampaign, 'status' | 'startsAt' | 'endsAt'>,
  now: Date = new Date(),
): CampaignPhase {
  if (c.status !== 'active') return 'inactive';
  if (new Date(c.startsAt).getTime() > now.getTime()) return 'scheduled';
  if (c.endsAt && new Date(c.endsAt).getTime() <= now.getTime()) return 'ended';
  return 'live';
}

/**
 * As regras como a página do participante e o convite recebem da API — para
 * a prévia ao vivo do formulário usar os MESMOS componentes das telas públicas.
 */
export function formToPublicRules(
  form: CampaignFormState,
  program: MgmProgram,
  format: (program: MgmProgram, amount: number) => string,
): MgmPublicRules {
  const role = (r: RoleForm) => {
    const amount = parseAmountInput(program, r.amount) ?? 0;
    return {
      enabled: r.enabled && amount > 0,
      amount,
      amountText: amount > 0 ? format(program, amount) : '',
      trigger: r.trigger,
    };
  };
  const payload = formToPayload(form, program);
  return {
    campaignId: '',
    program,
    startsAt: form.startsAt ? payload.startsAt : new Date().toISOString(),
    endsAt: payload.endsAt,
    referrer: role(form.referrer),
    referred: role(form.referred),
    minAmount: payload.purchaseRule.minAmount,
    withinDays: payload.purchaseRule.withinDays,
    title: form.title.trim() || DEFAULT_COPY.title,
    subtitle: form.subtitle.trim() || DEFAULT_COPY.subtitle,
  };
}
